import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Minimize2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const AIChatbot = ({ context = 'public', userId = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Welcome messages based on context
  const welcomeMessages = {
    public: {
      greeting: "Hi! I'm DBK Assistant, powered by Gemini AI.",
      subtitle: "I know all our projects, pricing, locations & buying process. Ask me anything about DBK Group!"
    },
    crm: {
      greeting: "Hi! I'm your DBK CRM Assistant, powered by Gemini AI.",
      subtitle: "I know the full CRM — attendance, Index 1/2, leave policies, all modules. What can I help with?"
    }
  };

  // Suggested questions based on context
  const suggestions = {
    public: [
      "What projects are currently ongoing?",
      "What's the price of Dreams Riviera?",
      "Where is Dream Kokan City located?",
      "How do I book a site visit?",
      "Tell me about completed projects",
      "What commercial projects are available?",
    ],
    crm: [
      "How do I update my attendance?",
      "How does Index 1 & 2 work?",
      "What are the attendance codes?",
      "How to export data to Excel?",
      "What is the leave policy?",
      "How to use Daily Schedule tab?",
    ]
  };

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message
      setMessages([{
        role: 'assistant',
        content: `${welcomeMessages[context].greeting}\n\n${welcomeMessages[context].subtitle}`
      }]);
    }
  }, [isOpen, context]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/api/chatbot/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: text,
          session_id: sessionId,
          context: context,
          user_id: userId
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "I apologize, but I'm having trouble responding right now. Please try again or contact our support team." 
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Connection error. Please check your internet and try again." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 z-50 bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group"
        data-testid="chatbot-trigger"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute -top-2 -left-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
          1
        </span>
        <span className="absolute left-16 top-1/2 -translate-y-1/2 bg-stone-800 text-white text-sm px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          Chat with us!
        </span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 left-6 z-50 bg-white rounded-2xl shadow-2xl transition-all duration-300 flex flex-col ${
        isMinimized ? 'w-72 h-14' : 'w-96 h-[600px] max-h-[80vh]'
      }`}
      data-testid="chatbot-window"
    >
      {/* ── Always-visible close button — absolute, never scrolled away ── */}
      <button
        onClick={() => setIsOpen(false)}
        data-testid="chatbot-close-fixed"
        title="Close chat"
        aria-label="Close chat"
        className="absolute top-2 right-2 z-50 w-8 h-8 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white shadow-lg transition-all hover:scale-110 active:scale-95"
      >
        <X className="w-4 h-4 font-bold" strokeWidth={3} />
      </button>

      {/* Header */}
      <div className="shrink-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-6 h-6" />
          </div>
          {!isMinimized && (
            <div>
              <h3 className="font-bold">DBK Support</h3>
              <p className="text-xs text-emerald-100">
                {context === 'public' ? 'Customer Support' : 'Employee Support'}
              </p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mr-8">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            data-testid="chatbot-minimize"
            title={isMinimized ? 'Expand' : 'Minimize'}
          >
            <Minimize2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 min-h-0 bg-stone-50">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex gap-2 mb-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.role === 'user' 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-stone-200 text-stone-600'
                }`}>
                  {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                </div>
                <div className={`max-w-[80%] p-3 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-emerald-600 text-white rounded-br-md'
                    : 'bg-white text-stone-700 rounded-bl-md shadow-sm'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-stone-200 text-stone-600 flex items-center justify-center">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm">
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="shrink-0 px-4 py-2 bg-stone-50 border-t">
              <p className="text-xs text-stone-500 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {suggestions[context].map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="text-xs bg-white border border-stone-200 px-3 py-1.5 rounded-full hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <form onSubmit={handleSubmit} className="shrink-0 p-4 bg-white border-t">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your message..."
                className="flex-1"
                disabled={isLoading}
                data-testid="chatbot-input"
              />
              <Button 
                type="submit" 
                disabled={isLoading || !inputValue.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-stone-400 mt-2 text-center">
              Powered by AI • DBK Group
            </p>
          </form>
        </>
      )}
    </div>
  );
};

export default AIChatbot;
